import {Button} from '@/components/ui/button/Button'
import {DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AgendaEventStatusDropdown} from '@/features/agenda/components/agenda-event-status-dropdown/AgendaEventStatusDropdown'
import {
    AgendaEventTreatmentSchema,
    EditEventFormValidationSchema,
    QuickEditEventFormValidationSchema
} from '@/features/agenda/formSchemas'
import {AgendaEventAppointment, EventExtended} from '@/features/agenda/types'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {useFormContext} from 'react-hook-form'
import {useMemo, useState} from 'react'
import {AgendaEventDetailsDialogMenu} from '@/features/agenda/components/agenda-event-details-dialog-menu/AgendaEventDetailsDialogMenu'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useAgendaFormDisabledOperation} from '@/features/agenda/hooks/useAgendaFormDisabledOperation'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {useGetEvent} from '@/features/agenda/queries/useGetEvent'
import {CreateMedicalRecordButton} from '@/features/medical-records/components/create-medical-record-button/CreateMedicalRecordButton'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {AgendaEditEventConfirmationDialog} from '@/features/agenda/components/agenda-event-form/agenda-edit-event-confirmation-dialog/AgendaEditEventConfirmationDialog'
import {usePermissions} from '@/features/auth/store/store'
import {PatientExtended} from '@/features/patients/types'
import {DeleteAgendaEventConfirmationDialog} from '@/features/agenda/components/delete-agenda-event-confirmation-dialog/DeleteAgendaEventConfirmationDialog'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {createDateTime} from '@/dayjs'
import {AgendaEventDetailsNavigateToMedicalRecord} from '../agenda-event-details-navigate-to-medical-record/AgendaEventDetailsNavigateToMedicalRecord'
import {agendaEventStatusMapToLabel} from '../../utils'

export const AgendaEventDetailsDialogFooter = ({
    agendaEvent,
    repetitionData
}: {
    agendaEvent: AgendaEventAppointment
    repetitionData?: AgendaEventTreatmentSchema['repetitionData']
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()

    const {data: patient, isLoading: isLoadingPatient} = useGetPatient({
        patientId: agendaEvent.eventPatients?.[0]?.patientId,
        responseType: 'extended',
        options: {
            enabled: agendaEvent.eventPatients?.length == 1 && !!agendaEvent.eventPatients?.[0]?.patientId
        }
    })

    const defaultEvent = useGetEvent({
        eventId: agendaEvent.id,
        responseType: 'extended',
        options: {
            enabled: false
        }
    })

    const disabledActions = useMemo(() => {
        return (
            (!permissions?.agenda.write && !permissions?.eventStatus.write) ||
            defaultEvent.data?.sector == 'domiciliary' ||
            defaultEvent.data?.status == 'completed' ||
            defaultEvent.data?.status == 'withdrawn' ||
            defaultEvent.data?.status == 'missed' ||
            defaultEvent.data?.status == 'cancelled' ||
            defaultEvent.data?.eventPatients?.some(ep => ep.project?.isSuspended)
        )
    }, [defaultEvent])

    return (
        <DialogFooter>
            <AgendaEventStatusDropdown
                eventStatus={agendaEvent.status}
                eventDateTime={createDateTime(agendaEvent.start, agendaEvent.time)}
                disabledStatuses={
                    !permissions?.agenda.write && permissions?.eventStatus.write
                        ? ObjectKeys(agendaEventStatusMapToLabel).filter(
                              status => status !== 'completed' && status !== 'missed' && status !== agendaEvent.status
                          )
                        : []
                }
                isDisabled={disabledActions}
            />
            <Flexbox gap={2}>
                {agendaEvent.sector == 'outpatient' && permissions?.agenda.write && (
                    <AgendaEventDetailsDialogMenu agendaEvent={agendaEvent} repetitionData={repetitionData} />
                )}
                <DialogDismiss render={<Button variant="secondary" size="sm" />}>{t('commons:cancel')}</DialogDismiss>
                <DialogFooterCta
                    agendaEvent={agendaEvent}
                    isLoading={defaultEvent.isLoading || isLoadingPatient}
                    patient={patient}
                    defaultEventData={defaultEvent.data}
                    hasSuspendedProject={defaultEvent.data?.eventPatients?.some(ep => ep.project?.isSuspended)}
                />
            </Flexbox>
        </DialogFooter>
    )
}

const DialogFooterCta = ({
    agendaEvent,
    patient,
    defaultEventData,
    hasSuspendedProject,
    isLoading
}: {
    agendaEvent: AgendaEventAppointment
    patient?: PatientExtended
    defaultEventData?: EventExtended
    hasSuspendedProject?: boolean
    isLoading?: boolean
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {handleSubmit, formState, reset, getValues} = useFormContext<QuickEditEventFormValidationSchema>()
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
    const dialog = useDialogContext()
    const confirmationDialog = useDialogStore({
        open: isConfirmationDialogOpen,
        setOpen: setIsConfirmationDialogOpen
    })
    const deleteDialog = useDialogStore()

    const isFormDirty = useMemo(() => ObjectKeys(formState.dirtyFields).length >= 1, [formState.dirtyFields])
    const areDisabledPatientRelatedFields = useAgendaFormDisabledOperation()

    const onSubmit = handleSubmit(
        () => {
            confirmationDialog.show()
        },
        error => {
            console.error(error)
        }
    )

    if (isLoading) {
        return <Skeleton width={100} height={28} />
    }

    if (
        (defaultEventData?.status == 'completed' || defaultEventData?.status == 'scheduled') &&
        !isFormDirty &&
        patient
    ) {
        if (!permissions?.medicalRecords.write) {
            return null
        }

        return (
            <>
                {!agendaEvent?.eventPatients?.[0]?.medicalRecord ? (
                    <CreateMedicalRecordButton
                        patient={patient}
                        navigateToCreateReport={true}
                        variant="primaryNeutral"
                        size="sm"
                        onSuccess={() => {
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT]})
                            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT]})
                            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT]})
                            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
                        }}
                        searchParams={{
                            projectId: defaultEventData.eventPatients?.[0]?.projectId ?? undefined,
                            medicalReportType:
                                agendaEvent.eventConsultants?.[0]?.accessType?.medicalRecordReportType || undefined
                        }}
                    >
                        {t('commons:report', {count: 1})}
                    </CreateMedicalRecordButton>
                ) : (
                    <AgendaEventDetailsNavigateToMedicalRecord patient={patient} agendaEvent={agendaEvent} />
                )}
            </>
        )
    }

    if (!permissions?.agenda.write && !permissions?.eventStatus.write) return null

    return (
        <>
            {hasSuspendedProject &&
            defaultEventData?.status != 'completed' &&
            defaultEventData?.status != 'withdrawn' &&
            defaultEventData?.status != 'missed' ? (
                <>
                    <Button
                        variant="primaryDanger"
                        onClick={() => deleteDialog.show()}
                        shape={'rectangle'}
                        size={'sm'}
                        fullWidth
                        style={{width: 'fit-content'}}
                    >
                        <Trash01Icon />
                        {t('commons:delete')}
                    </Button>
                    <DeleteAgendaEventConfirmationDialog
                        event={agendaEvent}
                        dialogStore={deleteDialog}
                        onDeletionSuccess={() => dialog?.hide()}
                    />
                </>
            ) : (
                <Button
                    variant="primaryNeutral"
                    size="sm"
                    disabled={!isFormDirty || areDisabledPatientRelatedFields}
                    onClick={onSubmit}
                >
                    {t('commons:save')}
                </Button>
            )}
            {isConfirmationDialogOpen && (
                <AgendaEditEventConfirmationDialog
                    confirmationDialog={confirmationDialog}
                    defaultValues={getValues() as EditEventFormValidationSchema}
                    isRecurringEvent={!!agendaEvent.frequency}
                    isToReschedule={formState.defaultValues?.status == 'to_reschedule'}
                    isChangedSubstitute={ObjectKeys(formState.dirtyFields).includes('substitute')}
                    parentOnSuccess={() => dialog?.hide()}
                    parentOnError={() => reset()}
                />
            )}
        </>
    )
}
